/**
 * Editor state.
 *
 * The project document itself lives in an `EditorDocument` (undo/redo, patches). This store mirrors
 * the current immutable project for React and holds UI-only state (selection, tool, zoom, open
 * sequences). All document edits must go through `edit()` so errors surface as toasts and the
 * history stays consistent.
 */
import { create } from 'zustand';
import { EditError, EditorDocument, type ApplyOptions, type Id, type Project } from '@ve/core';
import { toast } from './toast-store.ts';

export type Tool = 'select' | 'razor' | 'slip' | 'roll' | 'ripple';

export interface Selection {
  clipIds: Id[];
  transitionId: Id | null;
}

const EMPTY_SELECTION: Selection = { clipIds: [], transitionId: null };

interface EditorState {
  doc: EditorDocument | null;
  project: Project | null;
  /** Bumped on every document change; cheap dependency for effects. */
  version: number;
  canUndo: boolean;
  canRedo: boolean;
  undoLabel?: string;
  redoLabel?: string;

  activeSequenceId: Id | null;
  openSequenceIds: Id[];

  selection: Selection;
  tool: Tool;
  snapping: boolean;
  /** Timeline zoom: pixels per frame. */
  pxPerFrame: number;
  /** In/out range for loop playback and export, frames. */
  loopRange: { start: number; end: number } | null;
  loopEnabled: boolean;

  load(project: Project): void;
  close(): void;
  openSequence(id: Id): void;
  closeSequence(id: Id): void;
  select(selection: Partial<Selection>): void;
  toggleClipSelection(id: Id, additive: boolean): void;
  clearSelection(): void;
  setTool(tool: Tool): void;
  setSnapping(on: boolean): void;
  setZoom(pxPerFrame: number): void;
  setLoopRange(range: { start: number; end: number } | null): void;
  setLoopEnabled(on: boolean): void;
}

let unsubscribeDoc: (() => void) | null = null;

export const MIN_ZOOM = 0.02;
export const MAX_ZOOM = 40;

export const useEditor = create<EditorState>()((set, get) => ({
  doc: null,
  project: null,
  version: 0,
  canUndo: false,
  canRedo: false,
  activeSequenceId: null,
  openSequenceIds: [],
  selection: EMPTY_SELECTION,
  tool: 'select',
  snapping: true,
  pxPerFrame: 2,
  loopRange: null,
  loopEnabled: false,

  load(project) {
    unsubscribeDoc?.();
    const doc = new EditorDocument(project);
    const sync = () => {
      const s = get();
      // Drop selection entries that no longer exist (e.g. after undo).
      const p = doc.project;
      const sel = s.selection;
      const existing = new Set<Id>();
      for (const seq of Object.values(p.sequences)) {
        for (const t of [...seq.videoTracks, ...seq.audioTracks]) {
          for (const c of t.clips) existing.add(c.id);
          for (const tr of t.transitions) existing.add(tr.id);
        }
      }
      const clipIds = sel.clipIds.filter((id) => existing.has(id));
      const transitionId = sel.transitionId && existing.has(sel.transitionId) ? sel.transitionId : null;
      const openSequenceIds = s.openSequenceIds.filter((id) => p.sequences[id]);
      set({
        project: p,
        version: doc.version,
        canUndo: doc.canUndo,
        canRedo: doc.canRedo,
        undoLabel: doc.undoLabel,
        redoLabel: doc.redoLabel,
        selection:
          clipIds.length !== sel.clipIds.length || transitionId !== sel.transitionId
            ? { clipIds, transitionId }
            : sel,
        openSequenceIds,
        activeSequenceId:
          s.activeSequenceId && p.sequences[s.activeSequenceId] ? s.activeSequenceId : p.rootSequenceId,
      });
    };
    unsubscribeDoc = doc.subscribe(sync);
    set({
      doc,
      project,
      version: doc.version,
      activeSequenceId: project.rootSequenceId,
      openSequenceIds: [project.rootSequenceId],
      selection: EMPTY_SELECTION,
      canUndo: false,
      canRedo: false,
    });
  },

  close() {
    unsubscribeDoc?.();
    unsubscribeDoc = null;
    set({
      doc: null,
      project: null,
      activeSequenceId: null,
      openSequenceIds: [],
      selection: EMPTY_SELECTION,
    });
  },

  openSequence(id) {
    const { openSequenceIds } = get();
    set({
      activeSequenceId: id,
      openSequenceIds: openSequenceIds.includes(id) ? openSequenceIds : [...openSequenceIds, id],
      selection: EMPTY_SELECTION,
    });
  },

  closeSequence(id) {
    const { openSequenceIds, activeSequenceId, project } = get();
    if (!project || id === project.rootSequenceId) return;
    const next = openSequenceIds.filter((s) => s !== id);
    set({
      openSequenceIds: next,
      activeSequenceId:
        activeSequenceId === id ? (next[next.length - 1] ?? project.rootSequenceId) : activeSequenceId,
    });
  },

  select(selection) {
    set({ selection: { ...EMPTY_SELECTION, ...selection } });
  },

  toggleClipSelection(id, additive) {
    const { selection } = get();
    if (!additive) {
      set({ selection: { clipIds: [id], transitionId: null } });
      return;
    }
    const has = selection.clipIds.includes(id);
    set({
      selection: {
        clipIds: has ? selection.clipIds.filter((c) => c !== id) : [...selection.clipIds, id],
        transitionId: null,
      },
    });
  },

  clearSelection() {
    set({ selection: EMPTY_SELECTION });
  },

  setTool: (tool) => set({ tool }),
  setSnapping: (snapping) => set({ snapping }),
  setZoom: (pxPerFrame) => set({ pxPerFrame: Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, pxPerFrame)) }),
  setLoopRange: (loopRange) => set({ loopRange }),
  setLoopEnabled: (loopEnabled) => set({ loopEnabled }),
}));

/**
 * Applies an edit to the current document. Returns the recipe result, or `undefined` if the edit
 * was rejected (the reason is shown as a toast).
 */
export function edit<R>(label: string, recipe: (draft: Project) => R, options?: ApplyOptions): R | undefined {
  const doc = useEditor.getState().doc;
  if (!doc) return undefined;
  try {
    return doc.apply(label, recipe, options);
  } catch (err) {
    if (err instanceof EditError) toast.warn(err.message);
    else {
      // The document is unchanged (apply is atomic); report instead of crashing the handler.
      console.error(`Edit "${label}" failed`, err);
      toast.error(`${label} failed: ${err instanceof Error ? err.message : String(err)}`);
    }
    return undefined;
  }
}

/** Groups the edits of a continuous gesture (drag, scrub) into one undo step. */
export const gesture = {
  begin(label: string) {
    useEditor.getState().doc?.begin(label);
  },
  commit() {
    useEditor.getState().doc?.commit();
  },
  cancel() {
    useEditor.getState().doc?.rollback();
  },
  /**
   * Replaces the gesture's tentative edit: rolls back to the state at gesture start and applies
   * `recipe` from there. Lets absolute-position drags use overwrite semantics without destroying
   * clips they merely pass over. Returns false if the edit was rejected.
   */
  replace(label: string, recipe: (draft: Project) => void): boolean {
    const doc = useEditor.getState().doc;
    if (!doc) return false;
    doc.rollback();
    doc.begin(label);
    try {
      doc.apply(label, recipe);
      return true;
    } catch (err) {
      if (!(err instanceof EditError)) console.error(`Gesture "${label}" failed`, err);
      return false;
    }
  },
};

export function undo(): void {
  useEditor.getState().doc?.undo();
}

export function redo(): void {
  useEditor.getState().doc?.redo();
}

export function getProject(): Project {
  const p = useEditor.getState().project;
  if (!p) throw new Error('No project loaded');
  return p;
}

export function activeSequenceId(): Id {
  const s = useEditor.getState();
  return s.activeSequenceId ?? getProject().rootSequenceId;
}
